import React, { forwardRef, memo, useRef, useState } from 'react'
import PreviewIcon from 'assets/svgr-icons/Preview.svg'

import { Button, Popup } from 'common-components'
import { translate } from 'utils/translations'
import * as Styled from './styled'
import { formatVideoDuration } from './utils'
import { T } from 'consts/translationKeys'

interface IProps {
  src?: string
  duration: number
  onClick?: () => void
  fileName: string
}

const RecentlyUsedVideo = forwardRef<
  HTMLDivElement,
  IProps & React.HTMLAttributes<HTMLDivElement>
>(({ src, duration, onClick, fileName, ...rest }, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPreviewPopupVisible, setIsPreviewPopupVisible] = useState(false)
  const playPromiseRef = useRef<Promise<void> | null>(null)

  const handleMouseEnter = () => {
    if (videoRef.current) {
      playPromiseRef.current = videoRef.current?.play()
    }
  }

  const handleMouseLeave = () => {
    if (!videoRef.current) return
    if (playPromiseRef.current) {
      playPromiseRef.current.finally(() => {
        videoRef.current?.pause()
        playPromiseRef.current = null
      })
    } else {
      videoRef.current?.pause()
      videoRef.current.currentTime = 0
    }
  }

  const handleAdd = () => {
    if (onClick) onClick()
    setIsPreviewPopupVisible(false)
  }

  return (
    <Styled.RecentlyUsedVideoWrapper
      ref={ref}
      onClick={isPreviewPopupVisible ? undefined : onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      <Styled.RecentlyUsedVideo
        src={src}
        ref={videoRef}
        muted
        crossOrigin="anonymous"
        playsInline
      />
      <Styled.Duration>{formatVideoDuration(duration)}</Styled.Duration>
      <Styled.PreviewButton
        onClick={e => {
          e.stopPropagation()
          setIsPreviewPopupVisible(true)
        }}
      >
        <PreviewIcon />
      </Styled.PreviewButton>
      <Popup
        isOpen={isPreviewPopupVisible}
        onClose={() => setIsPreviewPopupVisible(false)}
      >
        <Styled.PreviewContentWrapper>
          <Styled.VideoPreviewPopupVideo src={src} controls />
          <Styled.VideoPreviewPopupFooter>
            <Styled.FileName>{fileName}</Styled.FileName>
            <Button onClick={handleAdd}>{translate(T.ADD_MEDIA_LABEL)}</Button>
          </Styled.VideoPreviewPopupFooter>
        </Styled.PreviewContentWrapper>
      </Popup>
    </Styled.RecentlyUsedVideoWrapper>
  )
})
RecentlyUsedVideo.displayName = 'RecentlyUsedVideo'

export default memo(RecentlyUsedVideo)
